import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useParams, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from './components/ScrollToTop';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import Dashboard from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import ClickLog from './pages/ClickLog';
import BlockedIPs from './pages/BlockedIPs';
import FraudAnalytics from './pages/FraudAnalytics';
import DetectionRules from './pages/DetectionRules';
import GoogleAdsSync from './pages/GoogleAdsSync';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import Agencies from './pages/Agencies';
import TotalProtectionSuite from './pages/TotalProtectionSuite';
import PPCShield from './pages/PPCShield';
import BotMitigation from './pages/BotMitigation';
import LeadShield from './pages/LeadShield';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import GDPR from './pages/GDPR';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CaseStudies from './pages/CaseStudies';
import FAQ from './pages/FAQ';
import KnowledgeCenter from './pages/KnowledgeCenter';
import KnowledgeCenterArticle from './pages/KnowledgeCenterArticle';
import Press from './pages/Press';
import Research from './pages/Research';
import Reviews from './pages/Reviews';
import WhyUs from './pages/WhyUs';
import Contact from './pages/Contact';
import Support from './pages/Support';
import About from './pages/About';
import Admin from './pages/Admin';
import Sidebar from './components/Sidebar';
import AIChatWidget from './components/AIChatWidget';
import { authAPI, campaignsAPI } from './utils/api';
import './App.css';

function KnowledgeCenterArticleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/knowledge-center/${slug}`} replace />;
}

const CAMPAIGN_KEY = 'cs_active_campaign';

function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const ACTIVE_PLANS = ['starter', 'pro', 'advanced'];

const GATE_BYPASS_PATHS = ['/settings', '/support', '/contact'];

const PROMO_CODES_GATE = {
  CLICK10:  10,
  CLICK50:  50,
  CLICK100: 100,
};

function applyDiscount(price, percent) {
  const raw = price * (1 - percent / 100);
  return raw % 1 === 0 ? `$${raw.toFixed(0)}` : `$${raw.toFixed(2)}`;
}

function SubscriptionGate({ user, children }) {
  const location = useLocation();
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, percent }
  const [promoMsg, setPromoMsg] = useState(null);
  const plan = (user?.plan || '').toLowerCase();
  if (ACTIVE_PLANS.includes(plan)) return children;
  if (GATE_BYPASS_PATHS.some(p => location.pathname.startsWith(p))) return children;

  const handleApplyPromo = () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) { setPromoMsg({ type: 'error', text: 'Enter a promo code' }); return; }
    const percent = PROMO_CODES_GATE[code];
    if (!percent) {
      setAppliedPromo(null);
      setPromoMsg({ type: 'error', text: 'Invalid promo code' });
      return;
    }
    setAppliedPromo({ code, percent });
    setPromoMsg({ type: 'success', text: `${percent}% off applied — will be used at checkout.` });
  };
  const handleClearPromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoMsg(null);
  };

  const billingUrl = appliedPromo
    ? `/settings?tab=billing&promo=${encodeURIComponent(appliedPromo.code)}`
    : '/settings?tab=billing';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', padding: 24 }}>
      <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, margin: '0 auto 20px' }}>
          <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg, #0969da, #08a86b)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600, fontSize: 16, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>ClickSaver</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Fraud Protection</div>
          </div>
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em', color: 'var(--text-primary)', marginBottom: 8 }}>Start your free trial</h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28, lineHeight: 1.6 }}>
          Choose a plan to start protecting your Google Ads campaigns from click fraud.<br />
          <strong style={{ color: 'var(--text-primary)' }}>7-day free trial</strong> — no charge until your trial ends.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          {[
            { name: 'Starter', base: 69, annual: 55, clicks: '10,000', campaigns: '1 campaign', color: '#475569' },
            { name: 'Pro', base: 109, annual: 87, clicks: '50,000', campaigns: '3 campaigns', color: '#2563eb', popular: true },
            { name: 'Advanced', base: 139, annual: 111, clicks: 'Unlimited', campaigns: 'Unlimited campaigns', color: '#059669' },
          ].map(p => {
            const priceStr = appliedPromo ? applyDiscount(p.base, appliedPromo.percent) : `$${p.base}`;
            const annualStr = appliedPromo ? applyDiscount(p.annual, appliedPromo.percent) : `$${p.annual}`;
            return (
              <div key={p.name} onClick={() => window.location.href = billingUrl} style={{
                padding: '14px 20px', borderRadius: 12, border: `2px solid ${p.popular ? p.color : 'var(--border)'}`,
                background: p.popular ? `rgba(37,99,235,0.06)` : 'var(--bg-card)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                transition: 'border-color 0.15s',
              }}>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{p.name}</span>
                    {p.popular && <span style={{ fontSize: 10, fontWeight: 700, background: p.color, color: '#fff', padding: '2px 8px', borderRadius: 20 }}>Most Popular</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.clicks} clicks · {p.campaigns}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'baseline', gap: 4, justifyContent: 'flex-end' }}>
                    {appliedPromo && <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', textDecoration: 'line-through' }}>${p.base}</span>}
                    <span>{priceStr}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span></span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{annualStr}/mo annual</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Promo Code panel */}
        <div style={{
          padding: 14, marginBottom: 20, borderRadius: 12,
          border: '1px dashed var(--border)', background: 'var(--bg-card)',
          textAlign: 'left',
        }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8 }}>
            Promo code
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={promoInput}
              onChange={e => setPromoInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
              placeholder="e.g. CLICK10"
              disabled={!!appliedPromo}
              style={{
                flex: 1, padding: '9px 12px', fontSize: 14, borderRadius: 8,
                border: '1px solid var(--border-strong)', background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', outline: 'none',
                textTransform: 'uppercase', letterSpacing: '0.03em',
                opacity: appliedPromo ? 0.6 : 1,
              }}
            />
            {appliedPromo ? (
              <button onClick={handleClearPromo} className="btn btn-ghost" style={{ padding: '8px 16px' }}>Remove</button>
            ) : (
              <button onClick={handleApplyPromo} className="btn btn-primary" style={{ padding: '8px 16px' }}>Apply</button>
            )}
          </div>
          {promoMsg && (
            <div style={{
              marginTop: 8, padding: '6px 10px', fontSize: 12, borderRadius: 6,
              background: promoMsg.type === 'error' ? 'rgba(220,38,38,0.08)' : 'rgba(22,163,74,0.08)',
              color: promoMsg.type === 'error' ? '#dc2626' : '#16a34a',
              border: `1px solid ${promoMsg.type === 'error' ? 'rgba(220,38,38,0.25)' : 'rgba(22,163,74,0.25)'}`,
            }}>
              {promoMsg.text}
            </div>
          )}
        </div>

        <a href={billingUrl} className="btn btn-primary" style={{ display: 'flex', width: '100%', padding: '12px', fontSize: 15, fontWeight: 500, textDecoration: 'none', marginBottom: 12, justifyContent: 'center' }}>
          Start 7-day free trial →
        </a>
        <button onClick={() => { signOut(auth); localStorage.clear(); }} style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [theme, setTheme] = useState('light');

  const loadCampaigns = useCallback(async () => {
    try {
      const raw = await campaignsAPI.list();
      const list = Array.isArray(raw) ? raw : [];
      setCampaigns(list);
      // Restore from localStorage, or auto-select first active campaign
      const saved = localStorage.getItem(CAMPAIGN_KEY);
      const match = list.find(c => c.id === saved);
      if (match) {
        setActiveCampaign(match.id);
      } else if (list.length > 0) {
        const first = list.find(c => c.is_active) || list[0];
        setActiveCampaign(first.id);
        localStorage.setItem(CAMPAIGN_KEY, first.id);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userData = await authAPI.me();
          setUser({ id: firebaseUser.uid, email: firebaseUser.email, name: firebaseUser.displayName || '', ...userData });
          applyTheme(userData.theme || 'light');
          await loadCampaigns();
        } catch {
          setUser({ id: firebaseUser.uid, email: firebaseUser.email, name: firebaseUser.displayName || '', theme: 'light' });
        }
      } else {
        setUser(null);
        setCampaigns([]);
        setActiveCampaign(null);
      }
      setLoading(false);
    });
    return unsub;
  }, [loadCampaigns]);

  const applyTheme = (t) => {
    setTheme(t);
    document.documentElement.setAttribute('data-theme', t);
  };

  const handleSelectCampaign = (id) => {
    setActiveCampaign(id);
    localStorage.setItem(CAMPAIGN_KEY, id);
  };

  const handleCampaignCreated = (newCampaign) => {
    setCampaigns(prev => {
      const exists = prev.find(c => c.id === newCampaign.id);
      return exists ? prev : [newCampaign, ...prev];
    });
    handleSelectCampaign(newCampaign.id);
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setCampaigns([]);
    setActiveCampaign(null);
    localStorage.removeItem(CAMPAIGN_KEY);
    applyTheme('light');
  };

  const handleThemeChange = async (newTheme) => {
    applyTheme(newTheme);
    try { await authAPI.updateSettings({ theme: newTheme }); } catch (_) {}
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
        <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
      </div>
    );
  }

  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/agencies" element={<Agencies />} />
        <Route path="/product/total-protection" element={<TotalProtectionSuite />} />
        <Route path="/product/ppc-shield" element={<PPCShield />} />
        <Route path="/product/bot-mitigation" element={<BotMitigation />} />
        <Route path="/product/lead-shield" element={<LeadShield />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms-policy" element={<TermsOfService />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/gdpr" element={<GDPR />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/knowledge-center" element={<KnowledgeCenter />} />
        <Route path="/knowledge-center/:slug" element={<KnowledgeCenterArticle />} />
        <Route path="/knowledge" element={<Navigate to="/knowledge-center" replace />} />
        <Route path="/knowledge/:slug" element={<KnowledgeCenterArticleRedirect />} />
        <Route path="/press" element={<Press />} />
        <Route path="/research" element={<Research />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/why-us" element={<WhyUs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/support" element={<Support />} />
        <Route path="/about" element={<About />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/*" element={
          <ProtectedRoute user={user}>
            <SubscriptionGate user={user}>
              <div className="app-shell">
                <Sidebar
                  campaigns={campaigns}
                  activeCampaign={activeCampaign}
                  onSelectCampaign={handleSelectCampaign}
                  user={user}
                  onLogout={handleLogout}
                />
                <main className="main-content" style={{ padding: 0 }}>
                  <AppTopHeader user={user} activeCampaign={campaigns?.find(c => c.id === activeCampaign)} />
                  <div style={{ padding: '16px 24px' }}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                    <Route path="/dashboard" element={<Dashboard campaignId={activeCampaign} campaigns={campaigns} user={user} />} />
                    <Route path="/campaigns" element={<Campaigns onSelect={handleSelectCampaign} onCreated={handleCampaignCreated} />} />
                    <Route path="/clicks" element={<ClickLog campaignId={activeCampaign} />} />
                    <Route path="/blocked" element={<BlockedIPs campaignId={activeCampaign} />} />
                    <Route path="/analytics" element={<FraudAnalytics campaignId={activeCampaign} />} />
                    <Route path="/rules" element={<DetectionRules campaignId={activeCampaign} user={user} />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/google-ads" element={<GoogleAdsSync campaignId={activeCampaign} />} />
                    <Route path="/reports" element={<Reports campaigns={campaigns} />} />
                    <Route path="/settings" element={<Settings theme={theme} setTheme={handleThemeChange} />} />
                    <Route path="/knowledge-center" element={<KnowledgeCenter />} />
                    <Route path="/knowledge-center/:slug" element={<KnowledgeCenterArticle />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/contact" element={<Contact />} />
                  </Routes>
                  <AppFooter />
                  </div>
                </main>
                <AIChatWidget />
              </div>
            </SubscriptionGate>
          </ProtectedRoute>
        } />
      </Routes>
    </BrowserRouter>
    </HelmetProvider>
  );
}

function AppTopHeader({ user, activeCampaign }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 50,
      height: 'var(--header-height)',
      display: 'flex', alignItems: 'center',
      gap: 16, padding: '0 24px',
      background: 'var(--bg-card)',
      borderBottom: '1px solid var(--border)',
    }}>
      {/* Search */}
      <div style={{
        flex: '1 1 400px', maxWidth: 520,
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'var(--bg-hover)', border: '1px solid transparent',
        borderRadius: 8, padding: '7px 12px', color: 'var(--text-muted)',
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <line x1="20" y1="20" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder={activeCampaign ? `Search in ${activeCampaign.name}…` : 'Search…'}
          style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, outline: 'none', color: 'var(--text-primary)', padding: 0 }}
        />
      </div>
      <div style={{ flex: 1 }} />
      {/* Bell */}
      <Link to="/notifications" style={{
        width: 32, height: 32, borderRadius: 8,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-secondary)', textDecoration: 'none',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      </Link>
      {/* Avatar */}
      <Link to="/settings" style={{
        width: 32, height: 32, borderRadius: '50%',
        background: 'linear-gradient(135deg, #0969da, #08a86b)',
        color: '#fff', fontWeight: 600, fontSize: 13,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        textDecoration: 'none',
      }}>
        {(user?.name || user?.email || '?').trim().charAt(0).toUpperCase()}
      </Link>
    </div>
  );
}

function AppFooter() {
  const col = { fontSize: 13, color: '#64748b', textDecoration: 'none', display: 'block', marginBottom: 8, transition: 'color .2s' };
  const label = { fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 };
  return (
    <footer style={{ background: '#0f172a', marginTop: 48, marginLeft: -24, marginRight: -24, padding: '40px 24px 24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 40, marginBottom: 40, maxWidth: 900 }}>
        <div>
          <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#fff', fontWeight: 800 }}>⊛</div>
            <span style={{ fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: 16, color: '#fff', letterSpacing: '-0.4px' }}>ClickSaver</span>
          </a>
          <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7, maxWidth: 220, margin: 0 }}>
            Real-time click fraud protection for Google Ads advertisers. Stop paying for fake clicks.
          </p>
        </div>
        <div>
          <div style={label}>Product</div>
          <a href="/#features" style={col}>Features</a>
          <a href="/#how-it-works" style={col}>How It Works</a>
          <Link to="/pricing" style={col}>Pricing</Link>
          <Link to="/agencies" style={col}>Agencies</Link>
          <Link to="/why-us" style={col}>Why ClickSaver?</Link>
        </div>
        <div>
          <div style={label}>Resources</div>
          <Link to="/blog" style={col}>Blog</Link>
          <Link to="/case-studies" style={col}>Case Studies</Link>
          <Link to="/faq" style={col}>FAQ</Link>
          <Link to="/knowledge-center" style={col}>Knowledge Center</Link>
          <Link to="/reviews" style={col}>Reviews</Link>
          <Link to="/support" style={col}>Support</Link>
          <Link to="/contact" style={col}>Contact</Link>
        </div>
        <div>
          <div style={label}>Company</div>
          <Link to="/about" style={col}>About Us</Link>
          <Link to="/press" style={col}>Press</Link>
          <Link to="/contact" style={col}>Contact</Link>
          <div style={{ ...label, marginTop: 16 }}>Legal</div>
          <Link to="/privacy-policy" style={col}>Privacy Policy</Link>
          <Link to="/terms-policy" style={col}>Terms of Service</Link>
          <Link to="/cookie-policy" style={col}>Cookie Policy</Link>
          <Link to="/gdpr" style={col}>GDPR</Link>
        </div>
      </div>
      <div style={{ borderTop: '1px solid #1e293b', paddingTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ fontSize: 13, color: '#334155' }}>© {new Date().getFullYear()} ClickSaver. All rights reserved.</span>
        <div style={{ display: 'flex', gap: 16 }}>
          <span style={{ fontSize: 12, color: '#334155' }}>🔒 Secured by Stripe</span>
          <span style={{ fontSize: 12, color: '#334155' }}>· GDPR Compliant</span>
        </div>
      </div>
    </footer>
  );
}
